import type { AuthSession, AuthUser } from '@/api/models';

const AUTH_SESSION_STORAGE_KEY = 'bos.auth.session';

const hasWindowStorage = () =>
    typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

/**
 * Parse the persisted JSON session. Anything that isn't a well-formed session
 * object (legacy bare-token strings, partial writes, corrupted JSON) is treated
 * as "no session" so callers never have to defend against half-shapes.
 */
const parseSession = (raw: string): AuthSession | null => {
    try {
        const parsed = JSON.parse(raw) as Partial<AuthSession> | null;
        if (
            parsed &&
            typeof parsed.accessToken === 'string' &&
            parsed.accessToken.length > 0 &&
            typeof parsed.refreshToken === 'string' &&
            parsed.refreshToken.length > 0 &&
            parsed.user !== undefined &&
            parsed.user !== null
        ) {
            return parsed as AuthSession;
        }
        return null;
    } catch {
        return null;
    }
};

export const getSession = (): AuthSession | null => {
    if (!hasWindowStorage()) {
        return null;
    }
    const raw = window.localStorage.getItem(AUTH_SESSION_STORAGE_KEY);
    if (!raw) {
        return null;
    }
    return parseSession(raw);
};

export const setSession = (session: AuthSession): void => {
    if (!hasWindowStorage()) {
        return;
    }
    window.localStorage.setItem(
        AUTH_SESSION_STORAGE_KEY,
        JSON.stringify(session),
    );
};

export const getAccessToken = (): string | null =>
    getSession()?.accessToken ?? null;

export const getRefreshToken = (): string | null =>
    getSession()?.refreshToken ?? null;

export const getUser = (): AuthUser | null => getSession()?.user ?? null;

export const clearAuthSession = (): void => {
    if (!hasWindowStorage()) {
        return;
    }
    window.localStorage.removeItem(AUTH_SESSION_STORAGE_KEY);
};

/**
 * Read a JWT's payload without verifying its signature — that's the server's
 * job. Client-side we only ever use this to decide what to render or whether a
 * request is worth firing; every real decision is re-made server-side.
 *
 * Returns null for anything undecodable, so callers fail closed.
 */
const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
    const payload = token.split('.')[1];
    if (!payload) {
        return null;
    }
    try {
        // JWT uses base64url; normalise to base64 before decoding.
        const normalised = payload.replace(/-/g, '+').replace(/_/g, '/');
        const decoded: unknown = JSON.parse(atob(normalised));
        return typeof decoded === 'object' && decoded !== null
            ? (decoded as Record<string, unknown>)
            : null;
    } catch {
        return null;
    }
};

/**
 * Decode the `exp` (seconds-since-epoch) claim. A token we can't decode is
 * treated as expired so we fail closed (redirect to login) rather than firing a
 * request we know will 401.
 */
const isJwtExpired = (token: string): boolean => {
    const decoded = decodeJwtPayload(token);
    if (!decoded || typeof decoded.exp !== 'number') {
        return true;
    }
    return decoded.exp * 1000 <= Date.now();
};

/**
 * How the current session was authenticated.
 *
 * `password` is a back-office sign-in; `pin` is a kiosk/POS token issued from a
 * 4-digit PIN. The distinction is load-bearing well beyond cosmetics: several
 * bos-api policies — including **both** tax-settings policies — require
 * `auth_method=password`, so a PIN token receives 403 on every one of those
 * endpoints regardless of the user's role.
 */
export type AuthMethod = 'password' | 'pin';

/**
 * The session's `auth_method` claim, or null when it can't be read.
 *
 * It lives only in the access token — `/auth/login` returns the principal
 * without it — so it is decoded here rather than stored. Null means "unknown",
 * and callers gating a password-only surface should treat unknown as *not*
 * password: showing a page that will 403 on every request is worse than showing
 * the explanation.
 */
export const getAuthMethod = (): AuthMethod | null => {
    const token = getAccessToken();
    if (!token) {
        return null;
    }
    const claim = decodeJwtPayload(token)?.auth_method;
    return claim === 'password' || claim === 'pin' ? claim : null;
};

/**
 * A *usable* session right now: an access token exists and its `exp` is still
 * in the future, so a request can be made without refreshing first. This is
 * deliberately NOT what the route guard keys off — see `hasRefreshableSession`.
 */
export const isAuthenticated = (): boolean => {
    const token = getAccessToken();
    return token !== null && !isJwtExpired(token);
};

/**
 * Whether there's a session worth mounting secured routes for. A well-formed
 * session always carries a refresh token, so its presence is enough: the
 * access JWT may already be expired, but the 401 interceptor will transparently
 * refresh it on the first request and replay. Keying the route guard off this
 * (rather than `isAuthenticated`) means a short-lived access token doesn't kick
 * a still-refreshable user out to /login on reload, deep link, or navigation —
 * the session is only abandoned when a refresh actually fails (interceptor) or
 * the user logs out.
 */
export const hasRefreshableSession = (): boolean => getSession() !== null;
